import { forwardRef, createElement, useState } from 'react';
import { ButtonVariant } from '../../Button';
import { useDesignTokens, DesignTokens, renderInlineCssVars } from '../../../theme';
import { BaseButton } from '../../Button/__internal__';
import { BaseToggleButtonProps } from '.';

import cx from 'classnames';
import cn from './BaseToggleButton.css.json';
import './BaseToggleButton.css';

export const BaseToggleButton = forwardRef<HTMLButtonElement, BaseToggleButtonProps>(
  function BaseToggleButton({ className, pressed = false, style, ...props }, ref) {
    const [isActive, setIsActive] = useState(false);
    // Unpressed ToggleButtons use the normal button styles. For pressed state we override Button's
    // tokens to use the correct pressed tokens.
    const dt = useDesignTokens();
    const classNames = [className, props.disabled && cn.disabled, pressed && cn.pressed, cn.toggle];
    const tokens = {
      ...renderInlineCssVars(getToggleButtonTokens(dt, pressed, !!props.disabled), 'button'),
      ...renderInlineCssVars(getToggleButtonTokens(dt, pressed, !!props.disabled), 'button-hover'),
      ...renderInlineCssVars(
        { ...getToggleButtonTokens(dt, true, !!props.disabled), filter: 'none' },
        'button-active'
      ),
      ...renderInlineCssVars(
        {
          outline:
            (pressed || isActive) && dt.themeVariant === 'high-contrast'
              ? 'var(--focus-dark-bg)'
              : 'var(--focus-light-bg)',
        },
        'button-focus'
      ),
    };

    // NOTE: aria-pressed is overridable, for a11y (check WAI ARIA 1.1 best practices)
    return (
      <BaseButton
        {...props}
        aria-pressed={pressed}
        className={cx(classNames)}
        ref={ref}
        style={{ ...tokens, ...style }}
        variant={props.text === undefined ? ButtonVariant.Icon : ButtonVariant.Light}
        onKeyDown={(e) => {
          if (!props.disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            setIsActive(true);
          }
        }}
        onKeyUp={(e) => {
          if (!props.disabled && (e.key === 'Enter' || e.key === ' ') && isActive) {
            e.preventDefault();
            setIsActive(false);
          }
        }}
      />
    );
  }
);

export const getToggleButtonTokens = (dt: DesignTokens, pressed: boolean, disabled: boolean) => {
  if (pressed) {
    if (disabled) {
      // Pressed disabled
      return dt.stateSecondaryNova.disabledSelected;
    } else {
      // Pressed enabled
      return dt.stateSecondaryNova.selected;
    }
  }
};
